import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import {
  fetchProfile,
  updateProfile,
  changePassword,
} from '../services/profileService';
import type {
  ProfileState,
  UpdateProfileRequest,
  ChangePasswordRequest,
} from '../types/profile.types';

const initialState: ProfileState = {
  profile: null,
  isLoading: false,
  isSaving: false,
  error: null,
};

export const loadProfile = createAsyncThunk(
  'settings/profile/load',
  async () => {
    return await fetchProfile();
  }
);

export const saveProfile = createAsyncThunk(
  'settings/profile/save',
  async (request: UpdateProfileRequest) => {
    return await updateProfile(request);
  }
);

export const submitChangePassword = createAsyncThunk(
  'settings/profile/changePassword',
  async (request: ChangePasswordRequest, { rejectWithValue }) => {
    try {
      await changePassword(request);
    } catch (err) {
      return rejectWithValue((err as Error).message);
    }
  }
);

const profileSlice = createSlice({
  name: 'settingsProfile',
  initialState,
  reducers: {
    clearProfileError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loadProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.profile = action.payload;
      })
      .addCase(loadProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message ?? 'Failed to load profile';
      })
      .addCase(saveProfile.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(saveProfile.fulfilled, (state, action) => {
        state.isSaving = false;
        state.profile = action.payload;
      })
      .addCase(saveProfile.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.error.message ?? 'Failed to save profile';
      })
      .addCase(submitChangePassword.rejected, (state, action) => {
        state.error =
          (action.payload as string) ??
          action.error.message ??
          'Failed to change password';
      });
  },
});

export const { clearProfileError } = profileSlice.actions;
export const profileReducer = profileSlice.reducer;
